

import './animations.css';
import { QuickAction } from '../quick-actions';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/src/lib/shadcn/cn';
import { useRouter } from 'next/navigation';

interface CampaignManagementRenderProps {
    actions: QuickAction[];
    onClose: () => void;
}

export default function CampaignManagementRender({
    actions,
    onClose
}: CampaignManagementRenderProps) {
    const router = useRouter();

    if (actions.length === 0) return null;

    const handleActionClick = (action: QuickAction) => {
        if (action.disabled) return;

        if (action.href) {
            window.location.href = action.href;
            onClose();
            return;
        }
        onClose();
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Campaign Management
                </h3>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
            </div>
            <div className="grid grid-cols-2 gap-2">
                {actions.map((action, index) => (
                    <button
                        key={action.text}
                        onClick={() => handleActionClick(action)}
                        className={cn(
                            "flex items-start gap-3 w-full text-left px-3 py-2 rounded-lg group",
                            "hover:bg-gray-100 dark:hover:bg-gray-700/50",
                            "text-gray-700 dark:text-gray-300 text-sm",
                            "campaign-item campaign-item-visible",
                            action.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent"
                        )}
                    >
                        <span className="mt-0.5 text-gray-500 dark:text-gray-400">
                            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500">
                                {action.icon}
                            </div>
                        </span>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">{action.text}</span>
                                    {action.badge && (
                                        <span className={cn(
                                            "rounded-md px-2 py-0.5 text-xs font-medium",
                                            action.isPopular
                                                ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400'
                                                : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                        )}>
                                            {action.badge}
                                        </span>
                                    )}
                                </div>
                                {action.newTabHref && !action.disabled && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(action.newTabHref, '_blank');
                                            onClose();
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    >
                                        <ExternalLink className="w-3 h-3 text-gray-500" />
                                    </button>
                                )}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{action.description}</span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
} 